import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, Trash2, Star } from "lucide-react";

const EMPTY = {
  id: null,
  name: "",
  city: "",
  quote: "",
  rating: 5,
  avatar_url: "",
  tier: "",
  order: 0,
  is_active: true,
};

export default function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/testimonials");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!editing.name.trim() || !editing.city.trim() || !editing.quote.trim()) {
      toast.error("Nama, Kota, dan Quote wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        city: editing.city.trim(),
        quote: editing.quote.trim(),
        rating: Number(editing.rating) || 5,
        avatar_url: editing.avatar_url.trim(),
        tier: editing.tier,
        order: Number(editing.order) || 0,
        is_active: !!editing.is_active,
      };
      if (editing.id) {
        await api.put(`/admin/testimonials/${editing.id}`, payload);
        toast.success("Testimoni diperbarui");
      } else {
        await api.post("/admin/testimonials", payload);
        toast.success("Testimoni ditambahkan");
      }
      setEditing(EMPTY);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus testimoni ini?")) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Testimoni dihapus");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="testimonials-tab">
      {/* Editor form */}
      <div className="bg-white border border-stone-200 p-5 md:p-8 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl text-[#0A0A0A]">
            {editing.id ? "Edit Testimoni" : "Tambah Testimoni"}
          </h3>
          {editing.id && (
            <button
              onClick={() => setEditing(EMPTY)}
              data-testid="testimonial-cancel"
              className="text-sm text-stone-500 hover:text-[#0A0A0A]"
            >
              Batal edit
            </button>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field
            label="Nama"
            testId="testimonial-name"
            value={editing.name}
            onChange={(v) => setEditing({ ...editing, name: v })}
            placeholder="Bunda Ratih"
          />
          <Field
            label="Kota"
            testId="testimonial-city"
            value={editing.city}
            onChange={(v) => setEditing({ ...editing, city: v })}
            placeholder="Surabaya"
          />
          <div className="md:col-span-2">
            <label className="block overline text-stone-500 mb-2">Quote</label>
            <textarea
              data-testid="testimonial-quote"
              rows={3}
              value={editing.quote}
              onChange={(e) => setEditing({ ...editing, quote: e.target.value })}
              placeholder="Ceritakan pengalaman mitra…"
              className="w-full px-3 py-2 border border-stone-300 focus:border-[#F39237] outline-none"
            />
          </div>
          <Field
            label="Rating (1-5)"
            testId="testimonial-rating"
            type="number"
            min={1}
            max={5}
            value={editing.rating}
            onChange={(v) => setEditing({ ...editing, rating: v })}
          />
          <div>
            <label className="block overline text-stone-500 mb-2">Tier</label>
            <select
              data-testid="testimonial-tier"
              value={editing.tier}
              onChange={(e) => setEditing({ ...editing, tier: e.target.value })}
              className="w-full h-11 px-3 border border-stone-300 focus:border-[#F39237] outline-none bg-white"
            >
              <option value="">— Tidak spesifik —</option>
              <option value="Reseller">Reseller</option>
              <option value="Agen">Agen</option>
              <option value="Distributor">Distributor</option>
            </select>
          </div>
          <Field
            label="Avatar URL (opsional)"
            testId="testimonial-avatar"
            value={editing.avatar_url}
            onChange={(v) => setEditing({ ...editing, avatar_url: v })}
            placeholder="https://…"
          />
          <Field
            label="Urutan"
            testId="testimonial-order"
            type="number"
            value={editing.order}
            onChange={(v) => setEditing({ ...editing, order: v })}
          />
          <label className="flex items-center gap-2 text-sm text-stone-700 md:col-span-2">
            <input
              type="checkbox"
              data-testid="testimonial-active"
              checked={editing.is_active}
              onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
              className="h-4 w-4"
            />
            Aktif (tampil di homepage)
          </label>
        </div>

        <button
          onClick={save}
          disabled={saving}
          data-testid="testimonial-save"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A] disabled:opacity-60"
        >
          {editing.id ? <Save size={16} /> : <Plus size={16} />}
          {saving ? "Menyimpan…" : editing.id ? "Simpan Perubahan" : "Tambah Testimoni"}
        </button>
      </div>

      {/* List */}
      <div className="bg-white border border-stone-200 p-5 md:p-8">
        <h3 className="font-display text-xl text-[#0A0A0A] mb-4">
          Daftar Testimoni ({items.length})
        </h3>
        {loading ? (
          <div className="text-stone-500 text-sm">Memuat…</div>
        ) : items.length === 0 ? (
          <div className="text-stone-500 text-sm">Belum ada testimoni.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((t) => (
              <article
                key={t.id}
                data-testid={`testimonial-row-${t.id}`}
                className={`border rounded-2xl p-5 ${t.is_active ? "border-stone-200" : "border-stone-200 bg-stone-50 opacity-70"}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="font-display text-base text-[#0A0A0A]">{t.name}</div>
                    <div className="text-xs text-stone-500">
                      {t.city}
                      {t.tier ? ` · ${t.tier}` : ""}
                      {" · #"}{t.order || 0}
                    </div>
                  </div>
                  <div className="flex gap-1 text-[#F39237]">
                    {Array.from({ length: Math.max(1, Math.min(5, t.rating || 5)) }).map((_, k) => (
                      <Star key={k} size={12} fill="currentColor" strokeWidth={0} />
                    ))}
                  </div>
                </div>
                <p className="text-sm text-stone-600 leading-snug line-clamp-3">{t.quote}</p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setEditing({ ...t })}
                    data-testid={`testimonial-edit-${t.id}`}
                    className="text-xs px-3 py-1.5 border border-stone-300 hover:border-[#F39237] hover:text-[#F39237]"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(t.id)}
                    data-testid={`testimonial-delete-${t.id}`}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-stone-300 hover:border-red-500 hover:text-red-500"
                  >
                    <Trash2 size={12} /> Hapus
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId, type = "text", min, max }) {
  return (
    <div>
      <label className="block overline text-stone-500 mb-2">{label}</label>
      <input
        type={type}
        min={min}
        max={max}
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-11 px-3 border border-stone-300 focus:border-[#F39237] outline-none"
      />
    </div>
  );
}
